import Dexie, { type EntityTable } from "dexie";

/**
 * IndexedDB database for import staging data.
 * This replaces the PostgreSQL import_* tables for local-first staging.
 * Data is only sent to Supabase on commit.
 */

// Token types matching the PostgreSQL enum
export type ImportTokenType = "period" | "product" | "store";

// Import metadata (equivalent to imports table)
export interface ImportMeta {
  id?: number; // Auto-incremented, but we'll only ever have 1 record
  userId: string;
  uploadStarted: string | null;
  uploadCompleted: string | null;
  error: string | null;
  notes: string | null;
}

// Token record (equivalent to import_tokens table)
export interface ImportToken {
  id?: number; // Auto-incremented
  type: ImportTokenType;
  reference: string;
  meta: Record<string, unknown> | null;
  exists?: boolean; // Whether the token exists in Supabase (for stores/products)
}

// Activity record (equivalent to import_activities table)
export interface ImportActivity {
  id?: number; // Auto-incremented
  typeId: number; // FK to marketing_activity_types (in Supabase)
  typeName: string; // Denormalized for display
  periodId: number; // FK to local ImportToken
  productId: number; // FK to local ImportToken
  budget: number | null;
  storeCount: number;
  problem: string | null; // Problems like missing stores/products
}

// Activity store assignment (equivalent to import_activity_stores table)
export interface ImportActivityStore {
  id?: number; // Auto-incremented
  activityId: number; // FK to local ImportActivity
  storeId: number; // FK to local ImportToken
  budget: number | null;
}

// Catalog product subset (cached from Supabase for local display)
export interface CatalogProduct {
  code: string; // Primary key (product code/reference)
  name: string;
  brandName: string | null;
  supplierName: string | null;
}

// Raw header mapping (column letter → header label from row 8)
export interface RawHeader {
  key: string; // Column letter (A, B, C, ... Z, AA, AB, ...)
  label: string; // Header name from row 8
}

// Raw row record (one per "Retenu" data row, keyed by column letters)
export interface RawRow {
  lineNumber: number; // Excel row number (PK)
  [column: string]: string | number; // Dynamic column-letter keys
}

// Dexie database class
class ImportDatabase extends Dexie {
  meta!: EntityTable<ImportMeta, "id">;
  tokens!: EntityTable<ImportToken, "id">;
  activities!: EntityTable<ImportActivity, "id">;
  activityStores!: EntityTable<ImportActivityStore, "id">;
  catalog!: EntityTable<CatalogProduct, "code">;
  rawHeaders!: EntityTable<RawHeader, "key">;
  rawRows!: EntityTable<RawRow, "lineNumber">;

  constructor() {
    super("promosight-import");

    this.version(1).stores({
      // Primary key is auto-incremented, indices for lookups
      meta: "++id, userId",
      tokens: "++id, type, reference, [type+reference]",
      activities: "++id, typeId, periodId, productId",
      activityStores: "++id, activityId, storeId",
    });

    // Version 2: Add exists field to tokens, problem field to activities
    this.version(2).stores({
      meta: "++id, userId",
      tokens: "++id, type, reference, [type+reference], exists",
      activities: "++id, typeId, periodId, productId, problem",
      activityStores: "++id, activityId, storeId",
    });

    // Version 3: Add catalog table for product/brand/supplier lookup
    this.version(3).stores({
      meta: "++id, userId",
      tokens: "++id, type, reference, [type+reference], exists",
      activities: "++id, typeId, periodId, productId, problem",
      activityStores: "++id, activityId, storeId",
      catalog: "code, supplierName",
    });

    // Version 4: Add raw data tables for Raw Data Analyser
    this.version(4).stores({
      meta: "++id, userId",
      tokens: "++id, type, reference, [type+reference], exists",
      activities: "++id, typeId, periodId, productId, problem",
      activityStores: "++id, activityId, storeId",
      catalog: "code, supplierName",
      rawHeaders: "key",
      rawRows: "lineNumber",
    });
  }
}

// Singleton instance
export const importDb = new ImportDatabase();

// Helper functions

/**
 * Check if there's an active import in progress
 */
export async function hasActiveImport(): Promise<boolean> {
  const count = await importDb.meta.count();
  return count > 0;
}

/**
 * Get the current import metadata
 */
export async function getImportMeta(): Promise<ImportMeta | undefined> {
  return importDb.meta.toCollection().first();
}

/**
 * Clear all import data from IndexedDB
 */
export async function clearImportData(): Promise<void> {
  await importDb.transaction(
    "rw",
    [
      importDb.meta,
      importDb.tokens,
      importDb.activities,
      importDb.activityStores,
      importDb.catalog,
      importDb.rawHeaders,
      importDb.rawRows,
    ],
    async () => {
      await importDb.activityStores.clear();
      await importDb.activities.clear();
      await importDb.tokens.clear();
      await importDb.meta.clear();
      await importDb.catalog.clear();
      await importDb.rawHeaders.clear();
      await importDb.rawRows.clear();
    },
  );
}

/**
 * Get token by ID
 */
export async function getToken(id: number): Promise<ImportToken | undefined> {
  return importDb.tokens.get(id);
}

/**
 * Get all tokens of a specific type
 */
export async function getTokensByType(
  type: ImportTokenType,
): Promise<ImportToken[]> {
  return importDb.tokens.where("type").equals(type).toArray();
}

/**
 * Get activities with pagination, filtering, and sorting
 */
export async function getActivities(options?: {
  limit?: number;
  offset?: number;
  periodIds?: number[];
  productIds?: number[];
  supplierNames?: string[];
  hasProblem?: "yes" | "no";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}): Promise<{ data: ImportActivity[]; totalCount: number }> {
  // If filtering by supplier, we need to first find which products belong to those suppliers
  let productIdsFromSupplier: number[] | undefined;
  if (options?.supplierNames && options.supplierNames.length > 0) {
    // Get products from catalog that match the supplier names
    const matchingCatalogProducts = await importDb.catalog
      .where("supplierName")
      .anyOf(options.supplierNames)
      .toArray();
    const matchingProductCodes = new Set(
      matchingCatalogProducts.map((p) => p.code),
    );

    // Get product token IDs that match these codes
    const productTokens = await importDb.tokens
      .where("type")
      .equals("product")
      .toArray();
    productIdsFromSupplier = productTokens
      .filter((t) => matchingProductCodes.has(t.reference))
      .map((t) => t.id!);
  }

  // Combine explicit productIds filter with supplier-derived productIds
  let effectiveProductIds = options?.productIds;
  if (productIdsFromSupplier !== undefined) {
    if (effectiveProductIds && effectiveProductIds.length > 0) {
      // Intersection: products must match both filters
      const supplierProductSet = new Set(productIdsFromSupplier);
      effectiveProductIds = effectiveProductIds.filter((id) =>
        supplierProductSet.has(id),
      );
    } else {
      effectiveProductIds = productIdsFromSupplier;
    }
    // If no products match the supplier filter, return empty
    if (effectiveProductIds.length === 0) {
      return { data: [], totalCount: 0 };
    }
  }

  let collection = importDb.activities.toCollection();

  // Apply filters
  if (options?.periodIds && options.periodIds.length > 0) {
    const periodIds = options.periodIds;
    collection = importDb.activities.where("periodId").anyOf(periodIds);
  }

  if (effectiveProductIds && effectiveProductIds.length > 0) {
    // If we already filtered by period, we need to filter the results
    const productIds = effectiveProductIds;
    if (options?.periodIds && options.periodIds.length > 0) {
      const items = await collection.toArray();
      let filtered = items.filter((a) => productIds.includes(a.productId));

      // Apply hasProblem filter
      if (options?.hasProblem) {
        filtered = filtered.filter((a) =>
          options.hasProblem === "yes"
            ? a.problem !== null
            : a.problem === null,
        );
      }

      const totalCount = filtered.length;

      // Apply sorting
      if (options?.sortBy) {
        const sortKey = options.sortBy as keyof ImportActivity;
        filtered.sort((a, b) => {
          const aVal = a[sortKey];
          const bVal = b[sortKey];
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;
          if (aVal < bVal) return options.sortOrder === "desc" ? 1 : -1;
          if (aVal > bVal) return options.sortOrder === "desc" ? -1 : 1;
          return 0;
        });
      }

      // Apply pagination
      const offset = options?.offset ?? 0;
      const limit = options?.limit ?? filtered.length;
      const data = filtered.slice(offset, offset + limit);

      return { data, totalCount };
    } else {
      collection = importDb.activities.where("productId").anyOf(productIds);
    }
  }

  // Get all items for sorting (Dexie sorting is limited)
  let items = await collection.toArray();

  // Apply hasProblem filter
  if (options?.hasProblem) {
    items = items.filter((a) =>
      options.hasProblem === "yes" ? a.problem !== null : a.problem === null,
    );
  }

  // Calculate totalCount after hasProblem filter (since Dexie can't query on it)
  const totalCount = items.length;

  // Apply sorting
  if (options?.sortBy) {
    const sortKey = options.sortBy as keyof ImportActivity;
    items.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (aVal < bVal) return options.sortOrder === "desc" ? 1 : -1;
      if (aVal > bVal) return options.sortOrder === "desc" ? -1 : 1;
      return 0;
    });
  }

  // Apply pagination
  const offset = options?.offset ?? 0;
  const limit = options?.limit ?? items.length;
  const data = items.slice(offset, offset + limit);

  return { data, totalCount };
}

/**
 * Get activity stores for a specific activity
 */
export async function getActivityStores(
  activityId: number,
): Promise<ImportActivityStore[]> {
  return importDb.activityStores
    .where("activityId")
    .equals(activityId)
    .toArray();
}

/**
 * Delete activities by IDs (and their associated stores)
 */
export async function deleteActivities(activityIds: number[]): Promise<void> {
  await importDb.transaction(
    "rw",
    [importDb.activities, importDb.activityStores],
    async () => {
      // Delete associated stores first
      await importDb.activityStores
        .where("activityId")
        .anyOf(activityIds)
        .delete();

      // Delete activities
      await importDb.activities.where("id").anyOf(activityIds).delete();
    },
  );
}

/**
 * Get all activity IDs (for select all functionality)
 */
export async function getAllActivityIds(options?: {
  periodIds?: number[];
  productIds?: number[];
  supplierNames?: string[];
  hasProblem?: "yes" | "no";
}): Promise<number[]> {
  // If filtering by supplier, we need to first find which products belong to those suppliers
  let productIdsFromSupplier: number[] | undefined;
  if (options?.supplierNames && options.supplierNames.length > 0) {
    // Get products from catalog that match the supplier names
    const matchingCatalogProducts = await importDb.catalog
      .where("supplierName")
      .anyOf(options.supplierNames)
      .toArray();
    const matchingProductCodes = new Set(
      matchingCatalogProducts.map((p) => p.code),
    );

    // Get product token IDs that match these codes
    const productTokens = await importDb.tokens
      .where("type")
      .equals("product")
      .toArray();
    productIdsFromSupplier = productTokens
      .filter((t) => matchingProductCodes.has(t.reference))
      .map((t) => t.id!);
  }

  // Combine explicit productIds filter with supplier-derived productIds
  let effectiveProductIds = options?.productIds;
  if (productIdsFromSupplier !== undefined) {
    if (effectiveProductIds && effectiveProductIds.length > 0) {
      // Intersection: products must match both filters
      const supplierProductSet = new Set(productIdsFromSupplier);
      effectiveProductIds = effectiveProductIds.filter((id) =>
        supplierProductSet.has(id),
      );
    } else {
      effectiveProductIds = productIdsFromSupplier;
    }
    // If no products match the supplier filter, return empty
    if (effectiveProductIds.length === 0) {
      return [];
    }
  }

  let collection = importDb.activities.toCollection();

  if (options?.periodIds && options.periodIds.length > 0) {
    collection = importDb.activities.where("periodId").anyOf(options.periodIds);
  }

  let items = await collection.toArray();

  if (effectiveProductIds && effectiveProductIds.length > 0) {
    items = items.filter((a) => effectiveProductIds!.includes(a.productId));
  }

  // Apply hasProblem filter
  if (options?.hasProblem) {
    items = items.filter((a) =>
      options.hasProblem === "yes" ? a.problem !== null : a.problem === null,
    );
  }

  return items.map((a) => a.id!).filter((id) => id !== undefined);
}

/**
 * Get filter options (unique periods and products from activities)
 */
export async function getFilterOptions(): Promise<{
  periods: { id: number; reference: string; exists?: boolean }[];
  products: {
    id: number;
    reference: string;
    exists?: boolean;
    supplierName?: string | null;
  }[];
  suppliers: { name: string }[];
}> {
  const activities = await importDb.activities.toArray();

  const periodIds = [...new Set(activities.map((a) => a.periodId))];
  const productIds = [...new Set(activities.map((a) => a.productId))];

  const periodTokens = await importDb.tokens
    .where("id")
    .anyOf(periodIds)
    .toArray();
  const productTokens = await importDb.tokens
    .where("id")
    .anyOf(productIds)
    .toArray();

  // Get catalog info for products
  const productCodes = productTokens.map((t) => t.reference);
  const catalogProducts = await importDb.catalog
    .where("code")
    .anyOf(productCodes)
    .toArray();
  const catalogByCode = new Map(catalogProducts.map((p) => [p.code, p]));

  // Get unique suppliers from catalog
  const supplierNames = new Set<string>();
  catalogProducts.forEach((p) => {
    if (p.supplierName) supplierNames.add(p.supplierName);
  });

  return {
    periods: periodTokens.map((t) => ({
      id: t.id!,
      reference: t.reference,
      exists: t.exists,
    })),
    products: productTokens.map((t) => {
      const catalog = catalogByCode.get(t.reference);
      return {
        id: t.id!,
        reference: t.reference,
        exists: t.exists,
        supplierName: catalog?.supplierName ?? null,
      };
    }),
    suppliers: Array.from(supplierNames)
      .sort()
      .map((name) => ({ name })),
  };
}
